import isEmpty from 'lodash-es/isEmpty'
import { reverse } from 'named-urls'
import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

import { DashboardBackButton } from '@components'
import { Dashboard, PageLayout, UnavailableResource } from '@components/common'
import { useDashboardPermissions } from '@components/utils/DashboardPermissions'
import {
  Banner,
  Dropdown,
  HeadContent,
  Loading,
  Subsection,
  Timeline,
  ToolbarButton,
  useMediaQueryContext,
} from '@fourthwall/components'
import { DotsHorizontalIcon } from '@fourthwall/icons'
import { TimezonedDateString } from '@modules/Timezone/components/TimezonedDateString'
import { routing } from '@utils/routing'

import * as S from './Order.styled'
import type { OrderShipment } from './Order.types'
import { CancelledOffers } from './components/CancelledOffers/CancelledOffers'
import { DonationItem } from './components/DonationItem/DonationItem'
import { OrderProfitSummary } from './components/OrderProfitSummary/OrderProfitSummary'
import { OrderSideSummary } from './components/OrderSideSummary/OrderSideSummary'
import { ShipmentItem } from './components/ShipmentItem/ShipmentItem'
import { useOrder } from './hooks/useOrder'
import { useOrderTimelineEvents } from './hooks/useOrderTimelineEvents'
import { useOrderCancellationQuery } from './queries/useOrderCancellationQuery'
import { useOrderRefundQuery } from './queries/useOrderRefundQuery'
import { getFullName } from './utils/getFullName'

export const Order = () => {
  const { orderId, order, banners, shippingMethodTooltip, isUnavailable, isLoading } = useOrder()
  const location = useLocation()
  const media = useMediaQueryContext()
  const navigate = useNavigate()
  const { hasPermission } = useDashboardPermissions()
  const { orderRefundQuery } = useOrderRefundQuery([orderId])
  const { orderCancellationQuery } = useOrderCancellationQuery([orderId])
  const timelineEnabled = hasPermission('contributions.orders.timelines')
  const { timelineEvents, isLoading: isLoadingTimelineEvents } = useOrderTimelineEvents(orderId)

  if (!order) {
    return <Loading isLoading={isLoading} />
  }

  const allShippedWithoutReshipments =
    order.shipments.filter(
      (shipment) => shipment.status !== 'NOT_SHIPPED' && shipment.reshipment == false,
    ) || []

  const getShipmentDeliveryIndex = (shipment: OrderShipment) =>
    allShippedWithoutReshipments.indexOf(shipment) + 1

  const getToolbarContent = () => {
    const fullyCancellable = orderCancellationQuery.data?.fullyCancellable
    const partiallyCancellable = orderCancellationQuery.data?.partiallyCancellable
    const refundable =
      orderRefundQuery.data?.remainingRefundAmount &&
      orderRefundQuery.data.remainingRefundAmount.value > 0

    const showEditOrderButton =
      (fullyCancellable || partiallyCancellable) && hasPermission('contributions.orders.edit')
    const showCancelOrderButton =
      (fullyCancellable || partiallyCancellable) && hasPermission('contributions.orders.cancel')
    const showRefundOrderButton = refundable && hasPermission('contributions.orders.refund')

    if (showEditOrderButton || showCancelOrderButton || showRefundOrderButton) {
      return (
        <Dropdown
          content={
            <>
              {showEditOrderButton && (
                <Dropdown.Item
                  label="Edit order"
                  onClick={() => {
                    navigate(reverse(routing.contributions.orders.order.edit, { orderId }))
                  }}
                />
              )}
              {showCancelOrderButton && (
                <Dropdown.Item
                  label="Cancel items"
                  onClick={() => {
                    navigate(reverse(routing.contributions.orders.order.cancel, { orderId }))
                  }}
                />
              )}
              {showRefundOrderButton && (
                <Dropdown.Item
                  label="Refund order"
                  onClick={() => {
                    navigate(reverse(routing.contributions.orders.order.refund, { orderId }))
                  }}
                />
              )}
            </>
          }
          position="bottom-end"
        >
          <ToolbarButton
            label="Settings"
            icon={DotsHorizontalIcon}
            size={media.tablet ? 'medium' : 'small'}
          />
        </Dropdown>
      )
    }

    return null
  }

  const renderReplacementNotification = () => {
    if (!order.replacement) return null

    if (order.replacement.type === 'Replacement') {
      return (
        <S.BannerWrapper>
          <Banner appearance="warning">
            This is a replacement order.{' '}
            <a
              href={reverse(routing.contributions.orders.order.self, {
                orderId: order.replacement?.sourceOrderId,
              })}
              target="_blank"
              rel="noreferrer"
            >
              View original order
            </a>
          </Banner>
        </S.BannerWrapper>
      )
    }

    return null
  }

  const renderReplacementSourceNotification = () => {
    if (!order.replacement) return null

    if (order.replacement.type === 'Source') {
      const hasManyReplacements = order.replacement?.replacementOrderIds?.length > 1
      const title = hasManyReplacements
        ? 'This order was issued replacements.'
        : 'This order was issued a replacement.'

      return (
        <S.BannerWrapper>
          <Banner appearance="warning">
            {title}{' '}
            {order.replacement?.replacementOrderIds?.map((replacementOrderId: any, index: any) => (
              <>
                <a
                  href={reverse(routing.contributions.orders.order.self, {
                    orderId: replacementOrderId,
                  })}
                  target="_blank"
                  rel="noreferrer"
                >
                  {hasManyReplacements
                    ? `Replacement order ${index + 1}`
                    : 'View replacement order'}
                </a>{' '}
              </>
            ))}
          </Banner>
        </S.BannerWrapper>
      )
    }

    return null
  }

  const renderLeftContent = () => (
    <>
      {renderReplacementNotification()}
      {renderReplacementSourceNotification()}
      {order && !order.profit && (
        <S.BannerWrapper>
          <Banner appearance="danger">
            Part of data relating to profit is not loading.{' '}
            <a href="mailto:hello@fourthwall.com">Contact our support</a>
          </Banner>
        </S.BannerWrapper>
      )}
      {banners &&
        banners.map((banner, index) => (
          <S.BannerWrapper>
            <Banner key={index} {...banner} />
          </S.BannerWrapper>
        ))}
      <Subsection title="Order summary" borderless>
        {order.shipments.map((shipment, index) => (
          <ShipmentItem
            key={index}
            shipment={shipment}
            createdAt={order.createdAt}
            deliveryIndex={getShipmentDeliveryIndex(shipment)}
            allDeliveriesCount={allShippedWithoutReshipments.length}
          />
        ))}
        {order && order.offersCancelled.length > 0 && (
          <CancelledOffers
            cancelledOffers={order.offersCancelled}
            cancellations={order.cancellations}
          />
        )}
        {order.donation && <DonationItem donation={order.donation} />}

        {order && (
          <OrderProfitSummary
            order={order}
            shippingMethodTooltip={shippingMethodTooltip}
            totalRefundedAmount={orderRefundQuery.data?.totalRefundedAmount}
          />
        )}
      </Subsection>
      {order.message && <Subsection title="Message left">{order.message}</Subsection>}
      {timelineEnabled && (
        <Subsection title="Timeline" borderless>
          <Loading isLoading={isLoadingTimelineEvents}>
            {isEmpty(timelineEvents) ? (
              'There are no events yet to display for this order.'
            ) : (
              <Timeline events={timelineEvents} size="large" />
            )}
          </Loading>
        </Subsection>
      )}
    </>
  )

  const getSectionName = () => {
    const orderCopy = order.type === 'CREATOR_BUDGET' ? 'samples order' : 'order'
    const fullName = getFullName(
      order.shipping.address?.firstName,
      order.shipping.address?.lastName,
    )
    const friendlyId = order.friendlyId

    let sectionName = orderCopy

    if (fullName) {
      const suffix = fullName.slice(-1).toLowerCase() === 's' ? `' ${orderCopy}` : `'s ${orderCopy}`
      sectionName = `${fullName}${suffix}`
    }

    if (friendlyId) {
      sectionName = `${sectionName} (#${friendlyId})`
    }

    return sectionName.charAt(0).toUpperCase() + sectionName.slice(1)
  }

  if (isUnavailable) {
    return <UnavailableResource resource="order" />
  }

  return (
    <>
      <HeadContent title={getSectionName()} />
      <Loading
        isLoading={isLoading || orderRefundQuery.isLoading || orderCancellationQuery.isLoading}
      >
        <PageLayout
          leftContent={renderLeftContent()}
          rightContent={<OrderSideSummary order={order} />}
        >
          <div>
            <Dashboard.ItemViewHeader
              name={getSectionName()}
              title={
                order.createdAt ? (
                  <TimezonedDateString
                    date={order.createdAt}
                    format="[Placed] MMM D, YYYY [at] h:mm A"
                  />
                ) : null
              }
              backButton={
                <DashboardBackButton
                  label="Orders"
                  to={{ pathname: routing.contributions.orders.all, search: location.search }}
                />
              }
              toolbar={getToolbarContent()}
            />
          </div>
        </PageLayout>
      </Loading>
    </>
  )
}
